import { scheduleEntryLabel, scheduleStagesFor } from '@pvplevelup/core/dist/tournament/TournamentSchedule';
import moment from 'moment';
import { ITournament, TournamentStatus, TournamentType, PickMode, ModeRule, FirstModeRule, TeamFormation, SubstituteRule, TournamentBracket } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { TOURNAMENT_TYPE_LABELS, BRACKET_LABELS, getTeamSize, getGrandPrixPoints, GRAND_PRIX_POINTS, getDungeonName } from '@pvplevelup/core/dist/tournament/TournamentRules';

/** Display helpers shared by the app and the website (no Electron here). */

export const TOURNAMENT_ACCENT = '#ffb300';

export const STATUS_INFO: Record<string, {label: string, color: string}> = {
  [TournamentStatus.SignupsOpen]: {label: 'Sign-ups open', color: '#66bb6a'},
  [TournamentStatus.SignupsClosed]: {label: 'Sign-ups closed', color: '#90a4ae'},
  [TournamentStatus.ReadyCheck]: {label: 'Ready check', color: '#ffb300'},
  [TournamentStatus.AwaitingDecision]: {label: 'Waiting on organizer', color: '#ff7043'},
  [TournamentStatus.Drafting]: {label: 'Captains drafting', color: '#29b6f6'},
  [TournamentStatus.Forming]: {label: 'Forming teams', color: '#42a5f5'},
  [TournamentStatus.Running]: {label: 'Live', color: '#ef5350'},
  [TournamentStatus.Completed]: {label: 'Completed', color: '#9575cd'},
  [TournamentStatus.Cancelled]: {label: 'Cancelled', color: '#757575'},
};

export const getStatusInfo = (status?: string | null) => STATUS_INFO[status || ''] || {label: status || 'Unknown', color: '#9e9e9e'};

export const TYPE_COLORS: Record<string, string> = {
  [TournamentType.GroupPvP]: '#ef5350',
  [TournamentType.Dueling]: '#ab47bc',
  [TournamentType.PvE]: '#26a69a',
};

export const typeLabel = (type?: string | null) => TOURNAMENT_TYPE_LABELS[type as TournamentType] || type || '';
export const bracketLabel = (bracket?: string | null) => BRACKET_LABELS[bracket as TournamentBracket] || bracket || '';

/** a scheduled start, short: "Sat 8:00 pm" (the viewer's own time zone) */
export const formatShortTime = (value?: string | null) => value ? moment(value).format('ddd h:mm a') : '';

export const formatDateTime = (value?: string | null) => value ? moment(value).format('ddd, MMM Do · h:mm a') : '–';

/** "in 3 days", "in 2h 15m", "starting now" */
export function formatCountdown(value?: string | null, now = Date.now()): string {
  if(!value)
    return '';
  const ms = new Date(value).getTime() - now;
  if(ms <= 0)
    return 'now';
  const minutes = Math.floor(ms / 60000);
  if(minutes < 60)
    return `in ${Math.max(1, minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if(hours < 48)
    return `in ${hours}h ${minutes % 60}m`;
  return `in ${Math.floor(hours / 24)} days`;
}

/** a running clock: 1:04:09, 42:10 or 0:05 */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600), m = Math.floor(total / 60) % 60, s = total % 60;
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * A cup team's clock while its round runs: the start window until its first fight, then its own time limit (like any
 * dungeon run, it counts from the first fight). Null once its run has ended.
 */
export function cupTeamClock(res: {finished?: boolean, startedAt?: string | null, startBy?: string | null, deadline?: string | null}, now: number):
  {kind: 'start' | 'run', ms: number} | null {
  if(res.finished)
    return null;
  if(res.startedAt && res.deadline)
    return {kind: 'run', ms: Math.max(0, new Date(res.deadline).getTime() - now)};
  if(res.startBy)
    return {kind: 'start', ms: Math.max(0, new Date(res.startBy).getTime() - now)};
  return null;
}

export const formatMinutes = (seconds?: number | null) => {
  if(seconds == null || seconds <= 0)
    return '–';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

/** "6v6 · premade" / "1v1" / "6-player teams" */
export function teamsLabel(t: Pick<ITournament, 'type' | 'teamSize' | 'teamFormation'>): string {
  if(t.type == TournamentType.Dueling)
    return '1v1';
  const size = getTeamSize(t);
  const formation = t.teamFormation == TeamFormation.Automatic ? 'teams made by the server' : 'premade teams';
  return t.type == TournamentType.PvE ? `Teams of ${size} · ${formation}` : `${size}v${size} · ${formation}`;
}

/** "Bo1 → Bo3 · semis Bo5 · final Bo7" */
export function seriesLabel(t: Pick<ITournament, 'series'>): string {
  const series = t.series;
  if(!series || series.rounds.length == 0)
    return '–';
  const parts = [series.rounds.map((b) => `Bo${b}`).join(' → ')];
  if(series.semifinal) parts.push(`semis Bo${series.semifinal}`);
  if(series.final) parts.push(`final Bo${series.final}`);
  return parts.join(' · ');
}

export interface RuleLine {
  label: string;
  value: string;
  hint?: string;
}

/** The rules as label / value lines, for the tournament page. */
export function getRuleLines(t: ITournament): RuleLine[] {
  // a shuffle (D30): players score on their own, with new teams every round
  if(t.bracket == TournamentBracket.Shuffle){
    const rounds = t.shuffleRounds || 4;
    return [
      {label: 'Format', value: `${typeLabel(t.type)} · Shuffle`, hint: 'Like Solo Shuffle: new, unique teams every round, a healer on each side, as even by MMR as possible.'},
      {label: 'Rounds', value: `${rounds} ${rounds == 1 ? 'game' : 'games'}, one per round`, hint: 'With exactly 6 players, at most 5. The next round opens a minute after a game ends.'},
      {label: 'Scoring', value: '1 point a win · ½ point sitting out', hint: 'With an odd number of players, one sits out each round, latest sign-ups first. A draw scores nothing. Equal points share a place.'},
      {label: 'Characters', value: `Up to ${t.maxCharacters} per player, level 80 on ${t.server}`, hint: 'A player counts as a healer if one of their characters is a healer.'},
      {label: 'Players', value: `${t.minParticipants} minimum · ${t.maxParticipants || 20} maximum`, hint: 'Ready players past the maximum are substitutes, in sign-up order.'},
      {label: 'Picks', value: t.pickMode == PickMode.Draft ? 'Draft, snake order' : 'Picked by the server',
        hint: t.pickMode == PickMode.Draft ? `${t.pickSeconds}s per pick. Each team's healer for the round picks a healer.` : 'The server picks each character, a healer for each team\'s healer.'},
      {label: 'Modes', value: (t.modes || []).join(', '), hint: (t.modes || []).length > 1 ? 'Each round\'s mode is random.' : undefined},
      {label: 'Maps', value: t.shuffleMap == 'Vote' ? 'The round\'s players vote' : 'Random each round', hint: t.shuffleMap == 'Vote' ? '30 seconds; most votes wins, ties are random.' : 'No map repeats until the pool runs out.'},
      {label: 'MMR', value: '4v4 and up: ranked · 3v3: unranked'},
      {label: 'Forfeit', value: `After ${t.forfeitMinutes} minutes missing`, hint: 'A player who doesn\'t check in misses the round and scores nothing.'},
      {label: 'Ready check', value: `Opens ${t.readyWindowMinutes} minutes before the start · ${t.graceMinutes} minutes grace`},
    ];
  }
  const lines: RuleLine[] = [
    {label: 'Format', value: `${typeLabel(t.type)} · ${bracketLabel(t.bracket)}`},
    {label: 'Teams', value: teamsLabel(t)},
    {label: 'Characters', value: `Up to ${t.maxCharacters} per player, level 80 on ${t.server}`, hint: 'Locked when you ready up, or at the start.'},
    {label: 'Players', value: `${t.minParticipants} minimum${t.maxParticipants ? ` · ${t.maxParticipants} maximum` : ''}`},
  ];
  if(t.type != TournamentType.Dueling)
    lines.push({label: 'Substitutes', value: t.substituteRule == SubstituteRule.LeaderPicks ? 'Team leader picks' : 'Automatic (sign-up order)',
      hint: "Players who aren't placed on a team are substitutes: they come in when a player leaves or is kicked."});
  if(t.type == TournamentType.GroupPvP || t.type == TournamentType.Dueling){
    lines.push({label: 'Series', value: seriesLabel(t)});
    lines.push({label: 'Picks', value: t.pickMode == PickMode.Draft ? `Draft${t.bans ? ` · ${t.bans} ${t.bans == 1 ? 'ban' : 'bans'} per team` : ''}` : 'Blind',
      hint: t.pickMode == PickMode.Draft ? `${t.pickSeconds}s per pick${t.bans ? `, ${t.banSeconds}s per ban` : ''}.` : "Nobody sees the other side's characters."});
  }
  if(t.type == TournamentType.GroupPvP){
    const modes = (t.modes || []).join(', ');
    lines.push({label: 'Modes', value: t.modeRule == ModeRule.LoserPicks
      ? `Loser picks: ${modes}`
      : modes,
      hint: t.modeRule == ModeRule.LoserPicks ? `Game 1: ${t.firstModeRule == FirstModeRule.Organizer ? t.firstMode : 'a random allowed mode'}.` : undefined});
    lines.push({label: 'Maps', value: `Assigned by the server for each match, so matches in a round use different maps${t.extraMaps ? ` · up to ${t.extraMaps} extra from the round's leftovers` : ''}`,
      hint: 'After game 1 the loser picks the map; a team can\'t pick a map it already won on.'});
  }
  if(t.bracket == TournamentBracket.DoubleElimination)
    lines.push({label: 'Grand final', value: t.bracketReset ? 'Bracket reset if the losers-bracket winner wins' : 'One series'});
  if(t.bracket == TournamentBracket.RoundRobin && t.groupSize)
    lines.push({label: 'Groups', value: `${t.groupSize} per group${t.playoffQualifiers ? ` · top ${t.playoffQualifiers} to the playoff` : ''}`});
  if(t.type == TournamentType.PvE){
    lines.push({label: 'Scoring', value: `${GRAND_PRIX_POINTS.slice(0, 4).join(', ')}, … points by place`, hint: 'Only verified runs that meet hard mode score; others get last place. Ties share a place.'});
    lines.push({label: 'Starting', value: `${t.cupStartMinutes ?? 10} minutes to start each dungeon`, hint: "A team that hasn't fought in the dungeon by then forfeits it. Each team's time limit runs from its own first fight."});
  }
  if((t.schedule || []).length > 0){
    const double = t.bracket == TournamentBracket.DoubleElimination;
    const order = (e: any) => scheduleStagesFor(t.type, t.bracket).indexOf(e.stage) * 1000 + (e.fromEnd ? 900 - e.fromEnd : (e.round || 1));
    lines.push({label: 'Schedule', value: (t.schedule || []).slice().sort((a, b) => order(a) - order(b))
      .map((e) => `${t.type == TournamentType.PvE ? `Dungeon ${e.round}` : scheduleEntryLabel(e, double)} · ${formatShortTime(e.at)}`).join(', '),
      hint: `A round never starts before its time; with no time it starts as soon as it's ready. Reminders go out ${t.reminderMinutes || 15} minutes before.`});
  }
  lines.push({label: 'Forfeit', value: `After ${t.forfeitMinutes} minutes missing`});
  lines.push({label: 'Ready check', value: `Opens ${t.readyWindowMinutes} minutes before the start · ${t.graceMinutes} minutes grace`});
  return lines;
}

export const dungeonLabel = (instance: number) => getDungeonName(instance);
export const placePoints = getGrandPrixPoints;
